import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { isZebraEnvEnabled } from "./zebraPrintEnabled";
import {
  DEFAULT_COMPANY_LABEL_PRINTING,
  LABEL_PRINTING_SETTING_KEY,
  parseCompanyLabelPrintingSettings,
} from "./labelPrintingSettings";
import type { CompanyLabelPrintingSettings } from "../types";

export const LABEL_PRINTING_QUERY_KEY = ["company_settings", LABEL_PRINTING_SETTING_KEY] as const;

interface UseLabelPrintingSettingsResult {
  settings: CompanyLabelPrintingSettings;
  loading: boolean;
  companyId: string | null;
}

/**
 * Read the company's `label_printing` policy from `company_settings`.
 * Falls back to `DEFAULT_COMPANY_LABEL_PRINTING` when no row exists yet.
 */
export function useLabelPrintingSettings(): UseLabelPrintingSettingsResult {
  const { id: companyId } = useCurrentCompany();

  const { data, isLoading } = useQuery({
    queryKey: [...LABEL_PRINTING_QUERY_KEY, companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<CompanyLabelPrintingSettings> => {
      // Members only need these two fields; never load the certificate or anything else.
      const { data } = await (supabase as any)
        .from("company_settings")
        .select("enabled:setting_value->enabled, defaultLabelSize:setting_value->>defaultLabelSize")
        .eq("company_id", companyId)
        .eq("setting_key", LABEL_PRINTING_SETTING_KEY)
        .maybeSingle();
      return parseCompanyLabelPrintingSettings(
        data ? { enabled: data.enabled, defaultLabelSize: data.defaultLabelSize } : null,
      );
    },
  });

  return {
    settings: data ?? { ...DEFAULT_COMPANY_LABEL_PRINTING },
    loading: isLoading,
    companyId,
  };
}

/**
 * Convenience selector combining the env kill-switch and company policy.
 * While the company query is still loading we return `{ enabled: false, loading: true }`
 * so callers can hide the Zebra option and avoid a UI flash.
 */
export function useZebraPrintEnabled(): { enabled: boolean; loading: boolean } {
  const envEnabled = isZebraEnvEnabled();
  const { settings, loading } = useLabelPrintingSettings();
  return { enabled: envEnabled && settings.enabled, loading };
}

/**
 * Invalidate the label_printing query after writing new company settings.
 */
export function useInvalidateLabelPrintingSettings() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: LABEL_PRINTING_QUERY_KEY });
}
